import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import MUIDataTable, { FilterType } from "mui-datatables";
import React, { useEffect, useState } from "react";
import { commonTableOptions } from "../../../constants";
import { API_RESULT, API_VERSION } from "../../../enums/common.enum";
import useAxiosInstance from "../../../hooks/useAxiosInstance";
import { handleError, parseAndFormateDate } from "../../../utils/common";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import UpdateCertModal from "../../Providers/updateCertModal";
import ProviderSettingsInfoModal from "../../Providers/providerSettingsInfoModal";
import useUserInfo from "../../../hooks/useUserInfo";
import { AuthenticatedUser } from "../../../interfaces/user.model";

const KycDetails = () => {
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const privateInstance = useAxiosInstance();
  const [updateCert, setUpdateCert] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState<string>("");
  const [showProviderSettingsInfoModal, setShowProviderSettingsInfoModal] =
    useState(false);
  const [filterBy, setFilterBy] = useState("pending");
  const user: AuthenticatedUser = useUserInfo();

  useEffect(() => {
    const getData = async () => {
      if (data?.length > 0) {
        setData([]);
      }
      setLoading(true);
      try {
        const result = await privateInstance.get(
          `${API_VERSION.V2}/analytics/digital-payments/providers?filter=${filterBy}`,
        );
        if (result?.data?.status === API_RESULT.SUCCESS) {
          setData(result?.data?.response?.kyc_providers);
        }
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy]);

  const cols = [
    {
      name: "location_id",
      label: "Location ID",
      options: {
        sort: false,
        filterType: "textField" as FilterType,
        customBodyRender: (value: any) => {
          return (
            <Typography
              sx={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#0058ff",
                textAlign: "left",
                minWidth: "180px",
              }}
              component="button"
              onClick={() => {
                setActiveLocationId(value);
                setShowProviderSettingsInfoModal(true);
              }}
              variant="body2"
            >
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "provider_name",
      label: "Pharmacy Name",
      options: {
        sort: false,
        filterType: "textField" as FilterType,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={160} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "created_by",
      label: "Created By",
      options: {
        sort: false,
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        sort: false,
        searchable: false,
        customBodyRender: (value: any) => {
          return (
            <>
              {value === "pending" ? (
                <Chip
                  size="small"
                  label="PENDING"
                  sx={{ backgroundColor: "#c7c7c7", padding: "6px" }}
                />
              ) : (
                <Chip
                  size="small"
                  label="REJECTED"
                  sx={{
                    backgroundColor: "#C53030",
                    color: "#FFF",
                    padding: "6px",
                  }}
                />
              )}
            </>
          );
        },
      },
    },
    {
      name: "rejection_reason",
      label: "Rejection Reason",
      options: {
        sort: false,
        filterType: "textField" as FilterType,
        customBodyRender: (value: any) => {
          const isEmpty =
            value === null ||
            value === undefined ||
            (typeof value === "string" && value.trim() === "");

          return (
            <Typography
              minWidth={160}
              variant="body2"
              align={isEmpty ? "center" : "left"}
              sx={{ color: isEmpty ? "text.disabled" : "text.primary" }}
            >
              {isEmpty ? "-" : value}
            </Typography>
          );
        },
      },
    },
    {
      name: "referral_source",
      label: "Referral",
      options: {
        sort: false,
        searchable: false,
      },
    },
    {
      name: "created_at",
      label: "Created At",
      options: {
        searchable: false,
        download: false,
        sort: true,
        sortCompare: (order: "asc" | "desc") => {
          const parseDate = (value: string) => {
            const [datePart, timePart] = value.split(" ");

            const [day, month, year] = datePart.split("/").map(Number);

            const [hours, minutes, secondsWithMs] = timePart.split(":");

            const [seconds, microseconds = "0"] = secondsWithMs.split(".");

            return new Date(
              year,
              month - 1,
              day,
              Number(hours),
              Number(minutes),
              Number(seconds),
              Number(microseconds.substring(0, 3)),
            ).getTime();
          };

          return (obj1: any, obj2: any) => {
            const date1 = parseDate(obj1.data);
            const date2 = parseDate(obj2.data);

            return order === "asc" ? date1 - date2 : date2 - date1;
          };
        },
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {parseAndFormateDate(value)}
            </Typography>
          );
        },
      },
    },
    {
      name: "created_at_date",
      label: "Created At",
      options: {
        sort: false,
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "license_certificate",
      label: "License Certificate",
      options: {
        sort: false,
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "gst_certificate",
      label: "GST Certificate",
      options: {
        sort: false,
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "blank_cheque",
      label: "Blank Cheque",
      options: {
        sort: false,
        searchable: false,
        display: false,
        download: true,
        customBodyRender: (value: any) => {
          return (
            <Typography minWidth={120} variant="body2">
              {value}
            </Typography>
          );
        },
      },
    },
    {
      name: "",
      label: "Actions",
      options: {
        sort: false,
        download: false,
        display: filterBy === "pending",
        customBodyRender: (value: any, tableMeta: any) => {
          return (
            <Box
              textAlign="center"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setActiveLocationId(data[tableMeta.rowIndex]?.location_id);
                setUpdateCert(true);
              }}
            >
              <PermIdentityIcon />
              <Typography
                sx={{
                  fontSize: "10px !important",
                  fontWeight: "600",
                  textTransform: "uppercase",
                }}
              >
                KYC
              </Typography>
            </Box>
          );
        },
      },
    },
  ];

  const CustomToolbar = () => {
    return (
      <FormControl size="small">
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          name="status"
          id="status"
          label="status"
          value={filterBy}
          onChange={(e) => setFilterBy(e.target.value)}
        >
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </Select>
      </FormControl>
    );
  };

  const options: any = {
    customToolbar: () => <CustomToolbar />,
    textLabels: {
      body: {
        noMatch: loading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    sort: true,
    searchPlaceholder: "Location ID/Name/Created By",
    filter: false,
    download: [
      "ravi@medpay.in",
      "arun@medpay.in",
      "tarun.yadav@medpay.in",
      "amit@medpay.in",
      "anuj@medpay.in",
      "onboardings@medpay.in",
      "joys.saji@medpay.in",
      "jai.srivastava@medpay.in",
      "anamika.shrivastava@medpay.in",
      "anirudh.agarwal@medpay.in",
      "sneha.jaiswal@medpay.in",
    ].includes(String(user?.user_id))
      ? true
      : false,
    downloadOptions: {
      filename: "Pending_KYC_" + new Date().toLocaleDateString(),
      filterOptions: {
        useDisplayedColumnsOnly: false,
        useDisplayedRowsOnly: true,
      },
    },
  };

  return (
    <Box>
      <MUIDataTable title="" data={data} columns={cols} options={options} />
      {updateCert ? (
        <UpdateCertModal
          updateCert={updateCert}
          setUpdateCert={setUpdateCert}
          activeLocationId={activeLocationId}
          callBack={() =>
            setData(
              data?.filter(
                (item: any) => item.location_id !== activeLocationId,
              ),
            )
          }
        />
      ) : null}
      {showProviderSettingsInfoModal && (
        <ProviderSettingsInfoModal
          showProviderSettingsInfoModal={showProviderSettingsInfoModal}
          setShowProviderSettingsInfoModal={setShowProviderSettingsInfoModal}
          activeLocationId={activeLocationId}
          providersData={null}
          setProvidersData={null}
          path="kyc-verify"
          targetId={activeLocationId}
        />
      )}
    </Box>
  );
};

export default KycDetails;
